import * as HotelMapConstants from './HotelMap';
import {
    ACCESSIBILITY_FILTER_PRESELECTION,
    ACCESSIBILITY_FILTER_VISIBILITY,
    ACCESSIBILITY_FILTER_UNCHECKED
} from './AccessibilityTypes';
import {
    USE_POINTS_FILTER_VISIBILITY,
    USE_POINTS_FILTER_UNCHECKED,
    USE_POINTS_FILTER_PRESELECTION
} from './UsePointsTypes';
import { PRICE_DISPLAY_OPTIONS, PRICE_DISPLAY_TAX_FEE_OPTIONS } from './PricingDisplay';
import { CRM_SIGN_IN_TYPE } from './crm-sign-in-type';
import { SAML_SSO_DEFAULT_VALUES } from './SamlSsoDefaultValues';
import { CRM_PROFILE_DEFAULT_VALUES } from './CrmProfileDefaultValues';
import { IN_CONTEXT_DISPLAY_TYPES } from './RoomsConfiguration';
import { IN_CONTEXT_BOOKING_STATUS } from './InContextBookingStatus';
import { merge } from 'lodash';
import { sbeKatanaTheme } from './themes/sbeKatanaTheme';
import { sbemShadCNTheme } from './themes/sbemShadCNTheme';

export const themeSystemDefault = sbeKatanaTheme;
export const sbemShadCNThemeDefault = sbemShadCNTheme;

export const configurationSystemDefault = merge({
    customContent: {},
    accessibilityFilter: {
        defaultSelection: ACCESSIBILITY_FILTER_PRESELECTION.ACCESSIBLE_NOT_CHECKED,
        unCheckedBehavior: ACCESSIBILITY_FILTER_UNCHECKED.SHOW_REGULAR_ONLY,
        visibility: ACCESSIBILITY_FILTER_VISIBILITY.NOT_SHOW_ACCESSIBLE_FILTERS
    },
    usePointsFilter: {
        defaultSelection: USE_POINTS_FILTER_PRESELECTION.USE_POINTS_NOT_CHECKED,
        unCheckedBehavior: USE_POINTS_FILTER_UNCHECKED.SHOW_REGULAR_ONLY,
        visibility: USE_POINTS_FILTER_VISIBILITY.NOT_SHOW_USE_POINTS_FILTERS
    },
    additionalGuestCountInclude: false,
    adultMaxQuantity: 19,
    allowBookOnBehalf: false,
    allowLogin: false,
    altAvailDistanceLimit: 30,
    altAvailDistanceLimitUnit: 'kilometers',
    altAvailSortOrder: 'distance',
    altAvailType: 'none',
    arrivalLeadDays: 0,
    arrivalTimeAlwaysRequired: false,
    arrivalTimeNotRequired: true,
    arrivalTimeRequiredIfEntered: false,
    autoExpandTaxDetails: false,
    calendarPriceDisplay: 'Minimum',
    childrenMaxAgeValue: 18,
    childrenMaxQuantity: 18,
    collectChildrenAge: false,
    configThresholdUrgencyTriggersForChain: {
        NoOfBookings: {
            Value: 1,
            Enabled: false
        },
        NoOfOnlookers: {
            Value: 1,
            Enabled: false
        },
        TimeElapsedSinceLastBooking: {
            Value: '18:20',
            Enabled: false
        },
        NoOfRemainingRooms: {
            Value: 5000,
            Enabled: false
        }
    }, // This Key will handle all the urgency triggers for every hotel in chain
    configThresholdUrgencyTriggersForHotel: {
        NoOfBookings: {
            Value: 30,
            Enabled: false
        },
        NoOfOnlookers: {
            Value: 3,
            Enabled: false
        },
        TimeElapsedSinceLastBooking: {
            Value: '18:20',
            Enabled: false
        },
        NoOfRemainingRooms: {
            Value: 5,
            Enabled: false
        }
    }, // This Key will handle all the urgency triggers for every room in hotel
    configThresholdUrgencyTriggersForRooms: {
        NoOfOnlookers: {
            Value: 3,
            Enabled: false
        },
        NoOfBookings: {
            Value: 30,
            Enabled: false
        },
        TimeElapsedSinceLastBooking: {
            Value: '18:20',
            Enabled: false
        },
        InHighDemand: {
            Value: 3,
            Enabled: false
        }
    }, // This Key will handle all the urgency triggers for every room in hotel
    configURL: '',
    coupons: {
        enabled: false
    },
    crm: {
        salesforceIntegrationKeys: {
            salesforceCommunityLink: '',
            salesforceClientId: '',
            salesforceStylesheetUrl: '',
            salesforceScriptUrl: '',
            salesforceEnableSignUp: false,
            salesforceSynxisSsoCrmApplicationId: ''
        },
        crmSignInType: CRM_SIGN_IN_TYPE.NONE
    },
    defaultLengthOfStay: 1,
    defaultProductView: 'splitView',
    roomAvailabilityDisplayType: IN_CONTEXT_DISPLAY_TYPES.ROOM_VIEW,
    defaultSplitViewType: 'room',
    disableChildrenSelection: false,
    disableUplift: false,
    displayAdditionalPolicy: false,
    displayChildrenPolicy: false,
    displayCommentArea: false,
    displayedFields: {
        createProfile: ['emailAddress', 'firstName', 'lastName'],
        guestDetails: ['emailAddress', 'firstName', 'lastName'],
        quickSignUp: ['emailAddress', 'firstName', 'lastName']
    },
    displayMemberRatesForExpiredMemberships: false,
    displayProductCancelAmount: false,
    displayProductDepositAmount: false,
    defaultProductSortSetting: 'recommended',
    displayVatBillingAddressFields: false,
    enableAdditionalGuestEmailAddresses: false,
    enableDayUse: false,
    enableExpressCheckout: false,
    enableGuestDataCollectionConsent: false,
    enableLoyaltyProgramTypes: false,
    enableMembershipExpirationMessage: true,
    enablePayByPoints: false,
    enableRoomCategoryImages: false,
    enableTripteasePriceCheckLite: true,
    enableWeatherGuaranteeBySensibleWeather: true,
    enableProtectGroupBookingProtection: false,
    excludeRoomCategoryImagesFromImageGalleries: false,
    enableTransportationDetails: false,
    enableUrgencyTriggerForHotelLevel: false,
    enableUrgencyTriggerForHotels: false,
    enableUrgencyTriggerForRooms: false,
    expandSpecialCodesAndRates: false,
    filterAltAvailResultsByBrand: false,
    getProductAvailabilityRequestPageSize: 200,
    hideHotelListingFilters: [],
    hideLoyaltyRatesForNotLoggedInUser: false,
    showPriceSummary: false,
    hideProductListingFilters: ['roomsAndSuites'],
    hideRoomShoppingFromMyBookingEngine: false,
    hotelMenuContentByLanguages: {},
    htmlArea: '',
    isGeoPromotionEnabled: false,
    isStoredStatusEnabled: false,
    layoutViewSetting: 'roomsAndRates',
    inContextNoOfProductsToExpand: '0',
    leadAvailabilityPriceDisplay: 'MinimumAvg',
    mapOnChainPage: {
        hotelListDisplay: HotelMapConstants.HOTEL_LIST_VIEW_ONLY,
        numberOfFetchedHotels: 200,
        center: {
            lat: 0,
            lng: 0
        },
        zoom: 12,
        fitMapToHotels: false,
        searchRadius: 300,
        destinations: []
    },
    maximumNumberOfNights: 365,
    maximumNumberOfRoomsPerItinerary: 0,
    maxQuantityPerRoom: 37,
    enableMultiRoomFlow: false,
    monthsOfInventory: 11,
    onHoldEnabled: false,
    optionalFieldList: [],
    policyUrls: {},
    pricingDisplay: PRICE_DISPLAY_OPTIONS.BOTH_PRICES_NIGHTLY_FIRST,
    pricingDisplayTaxAndFeeOption: PRICE_DISPLAY_TAX_FEE_OPTIONS.INCLUDE_TAXES_FEES,
    priceSummaryExpanded: true,
    productSegmentationFilter: 'dontShow',
    requiredFieldList: ['emailAddress', 'firstName', 'lastName'],
    requiredFields: {
        createProfile: ['emailAddress', 'firstName', 'lastName'],
        guestDetails: ['emailAddress', 'firstName', 'lastName'],
        quickSignUp: ['emailAddress', 'firstName', 'lastName']
    },
    resumeBookings: true,
    resumeBookingDisplayType: 'bottomRight',
    inContextOverlayWidth: 'fullPage',
    retailProductCategoriesToDisplay: [],
    roomCategoryExpandOption: 'collapseAll',
    roomCategoryViewOption: 'showIndividualRoomCards',
    roomCategoriesExactMatch: false,
    roomUpsellPriceDisplay: 'averagePerNightDifference',
    SampleBoolean: false,
    shoppingCartExpirationDays: 30,
    SHOW_TAX_FEE_DETAIL_IN_PRICE_BREAKDOWN: true,
    showAdditionalGuests: false,
    showAddOns: false,
    showAddOnCrossSellInProductDetails: false,
    showAddOnCrossSellInAddedToCartOverlay: false,
    showAddOnCrossSellOnCartPage: false,
    showAddOnCrossSellOnConfirmationPage: false,
    showMemberRatesWhenOtherAvailableRatesAreHidden: false,
    showAllAvailableRatesWithPromo: true,
    showPromoOrGroupCode: true,
    showGroupCodeOption: true,
    showAgentId: true,
    requestedRatesSorting: 'RequestedPrivateThenRequestedPublicThenPublicRates',
    showAllAvailableRatesWithRateFilters: true,
    showAllAvailableRatesWithIata: true,
    showAvailableHotelsOnly: true,
    showCalendarLegend: true,
    showDetailedPriceAmount: 'decimals',
    showHeroImage: {
        productSelection: false,
        addOns: false,
        guestDetails: false,
        review: false,
        confirmation: false,
        searchResults: false,
        hotelSelection: false
    },
    showhtmlArea: true,
    showIcons: {
        guarantee: {
            freeCancellation: false,
            holdAvailable: false,
            depositRequired: false,
            guaranteedWithCreditCard: false,
            instantHold: false
        },
        mealPlan: {
            breakfastIncluded: false,
            breakfastDinnerIncluded: false,
            dinnerIncluded: false,
            discountedMealsForChildren: false,
            allMealsIncluded: false
        },
        roomAmenity: {
            accessibleFeatures: {
                accessibleRoom: false
            },
            airQuality: {
                airConditioning: false,
                nonSmoking: false,
                smoking: false
            },
            bathroom: {
                bathtub: false,
                hairDryer: false,
                shower: false
            },
            internetServices: {
                freeWifi: false,
                highSpeedInternet: false
            },
            kitchen: {
                kitchenArea: false,
                microwave: false,
                refrigerator: false
            },
            pets: {
                petsAllowed: false
            },
            workspacesComfortSecurity: {
                deskOrWorkspace: false,
                firePlace: false,
                ironInRoom: false,
                safeInRoom: false,
                sittingArea: false,
                washerAndDryer: false
            }
        }
    },
    showMarketingMessage: false,
    showOnlyAssignedRatesToManagedLoyaltyProgramMembers: false,
    showRateFilters: false,
    showRateImage: true,
    inContextShowRateImage: true,
    inContextEnableCategoryViews: false,
    inContextExcludeRoomCategoryImagesFromImageGalleries: false,
    inContextRoomCategoryViewOption: 'showRoomsGrouping',
    inContextRoomCategoryExpandOption: 'expandAll',
    inContextBookingStatus: IN_CONTEXT_BOOKING_STATUS.INACTIVE,
    showRoomCategories: false,
    showRoomRequests: false,
    showShoppingPriceAmount: 'roundup',
    displayOrderCommentArea: false,
    signInForBenefits: {
        inUse: false,
        loyaltyAssignments: []
    },
    splitViewExpandOption: 'expandFirstProduct',
    trackingHooks: {
        head: '',
        GET_PRODUCT_AVAILABILITY_RESPONDED: '',
        GET_ADD_ONS_SUCCESS: '',
        CREATE_RESERVATION_SUCCESS: '',
        CONFIRM_RESERVATION_SUCCESS: '',
        ON_CHECKOUT: '',
        ON_REVIEW: '',
        ON_LANDING_PAGE: '',
        RETAIL_PRODUCT_DETAILS: ''
    },
    travelAgencyDisplayName: 'displayTravelAgencyCompanyName',
    urgencyTriggersTimePeriod: {
        TimePeriod: {
            Hours: 24
        }
    },
    videoEnable: true,
    videoPaths: [],
    viewCartBeforeCheckout: false
}, CRM_PROFILE_DEFAULT_VALUES, SAML_SSO_DEFAULT_VALUES);



// WEBPACK FOOTER //
// ./src/constants/SystemDefaults.js